import { Helmet } from "@dr.pogodin/react-helmet";
import { Link } from "react-router-dom";
import RecentPosts from "../components/RecentPosts";
import ExpenseTrackerCTA from "../components/ExpenseTrackerCTA";

interface Tool {
  title: string;
  description: string;
  link: string;
  icon: string;
  popular: boolean;
  premium?: boolean;
  tags: string[];
}

export default function ToolsIndex() {
  const tools: Tool[] = [
    {
      title: "1099 Tax Calculator",
      description: "Calculate federal, state, and self-employment taxes with deductions, retirement contributions, and quarterly estimates.",
      link: "/",
      icon: "🧮",
      popular: true,
      tags: ["Tax Planning", "Quarterly Taxes", "Deductions"]
    },
    {
      title: "Expense Tracker & Audit Risk",
      description: "Track business expenses with real-time audit risk scoring. Know exactly which deductions are safe and what documentation you need.",
      link: "/tools/expense-tracker",
      icon: "💰",
      popular: true,
      premium: true,
      tags: ["Expense Tracking", "Audit Defense", "Documentation"]
    },
    {
      title: "Invoice Manager & Payments",
      description: "Create professional invoices, track payments, and get paid faster with Stripe integration. Perfect for freelancers and consultants.",
      link: "/tools/invoices",
      icon: "📄",
      popular: true,
      premium: true,
      tags: ["Invoicing", "Payments", "Client Management"]
    },
    {
      title: "Quarterly Tax Deadlines",
      description: "Never miss a payment deadline. View the complete 2025 estimated tax payment schedule with due dates and payment options.",
      link: "/tools/quarterly-tax-deadlines",
      icon: "📅",
      popular: true,
      tags: ["Deadlines", "IRS", "Form 1040-ES"]
    },
    {
      title: "Mileage Log & Calculator",
      description: "Track business miles, calculate IRS standard mileage deductions, and download your log for tax time.",
      link: "/tools/mileage-log",
      icon: "🚗",
      popular: true,
      tags: ["Deductions", "Vehicle Expenses", "Record Keeping"]
    },
    {
      title: "State Tax Comparison",
      description: "Compare income tax rates across all 50 states. Find the best location for your freelance business.",
      link: "/tools/state-tax-comparison",
      icon: "🗺️",
      popular: false,
      tags: ["State Taxes", "Relocation", "Tax Planning"]
    },
    {
      title: "W-2 vs Freelance Calculator",
      description: "Calculate how much you need to earn as a freelancer to replace your W-2 job salary and benefits.",
      link: "/tools/break-even-calculator",
      icon: "⚖️",
      popular: true,
      tags: ["Career Planning", "Salary Comparison", "Benefits"]
    },
    {
      title: "Retirement Calculator",
      description: "Plan your retirement savings with inflation-adjusted projections for Solo 401(k), SEP IRA, and traditional accounts.",
      link: "/retirement",
      icon: "💰",
      popular: false,
      tags: ["Retirement", "Long-term Planning", "Savings"]
    },
    {
      title: "Inflation Calculator",
      description: "Analyze historical CPI data and project future purchasing power to plan for inflation in your retirement.",
      link: "/calculators/inflation",
      icon: "📈",
      popular: false,
      tags: ["Inflation", "CPI", "Financial Planning"]
    }
  ];

  const popularTools = tools.filter(t => t.popular);
  const allTools = tools;

  return (
    <>
      <Helmet>
        <title>Free Tax Tools for 1099 Contractors & Freelancers | Free1099Calc</title>
        <meta
          name="description"
          content="Free calculators and tools for self-employed professionals: tax calculators, mileage logs, quarterly deadline reminders, state tax comparisons, and more."
        />
        <meta name="keywords" content="1099 tools, freelance calculators, self employment tools, tax planning tools, mileage tracker" />
        <link rel="canonical" href="https://free1099calc.com/tools" />
      </Helmet>

      {/* Hero Section */}
      <div className="relative bg-gradient-to-br from-primary-900 via-primary-800 to-slate-900 overflow-hidden">
        <div 
          className="absolute inset-0 opacity-15"
          style={{
            backgroundImage: 'url("https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=1920&q=80")',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-primary-900/85 to-slate-900/90" />
        
        <div className="relative max-w-6xl mx-auto px-4 py-20 md:py-28">
          <div className="text-center max-w-3xl mx-auto">
            <div className="inline-block px-5 py-2.5 bg-white/15 backdrop-blur-sm border border-white/30 rounded-full mb-6">
              <span className="text-white text-sm font-bold">🛠️ TOOLS & CALCULATORS</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
              Free Tax Tools for Freelancers
            </h1>
            <p className="text-xl md:text-2xl text-slate-300 mb-8">
              Everything you need to manage taxes, track expenses, and plan your finances as a 1099 contractor.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/"
                className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-8 py-4 rounded-lg font-semibold transition-colors shadow-lg"
              >
                Calculate Taxes →
              </Link>
              <Link
                to="/about"
                className="inline-block bg-white/10 hover:bg-white/20 backdrop-blur-sm text-white border border-white/30 px-8 py-4 rounded-lg font-semibold transition-colors"
              >
                Learn More
              </Link>
            </div>
          </div>
        </div>
      </div>

    <div className="min-h-screen bg-slate-50">
      <div className="max-w-6xl mx-auto px-4 py-12">

        {/* Expense Tracker CTA - Featured at top */}
        <section className="mb-12">
          <ExpenseTrackerCTA />
        </section>

        {/* Popular tools */}
        <section className="mb-12">
          <h2 className="text-3xl font-black text-gray-900 mb-8 flex items-center gap-3">
            <span className="text-primary-600">⭐</span>
            Most Popular Tools
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {popularTools.map((tool) => (
              <Link
                key={tool.link}
                to={tool.link}
                className="bg-white rounded-3xl border-2 border-primary-200 shadow-xl p-8 hover:shadow-2xl hover:border-primary-400 transition-all group relative"
              >
                {tool.premium && (
                  <div className="absolute top-4 right-4">
                    <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-gradient-to-r from-yellow-400 to-orange-400 text-xs font-black text-white rounded-full shadow-lg">
                      ⭐ PREMIUM
                    </span>
                  </div>
                )}
                <div className="text-5xl mb-4">{tool.icon}</div>
                <h3 className="text-xl font-black text-gray-900 mb-3 group-hover:text-primary-700 transition-colors">
                  {tool.title}
                </h3>
                <p className="text-sm text-slate-600 mb-4">
                  {tool.description}
                </p>
                <div className="flex flex-wrap gap-2">
                  {tool.tags.map((tag) => (
                    <span key={tag} className="text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* All tools */}
        <section className="mb-12">
          <h2 className="text-3xl font-black text-gray-900 mb-8 flex items-center gap-3">
            <span className="text-primary-600">📋</span>
            All Tools
          </h2>
          <div className="bg-white rounded-3xl border-2 border-gray-200 shadow-xl divide-y divide-gray-100">
            {allTools.map((tool) => (
              <Link
                key={tool.link}
                to={tool.link}
                className="flex items-center p-6 hover:bg-slate-50 transition-colors group"
              >
                <div className="text-3xl mr-4">{tool.icon}</div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h3 className="text-lg font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">
                      {tool.title}
                    </h3>
                    {tool.premium && (
                      <span className="inline-flex items-center px-2 py-0.5 bg-gradient-to-r from-yellow-400 to-orange-400 text-xs font-bold text-white rounded-full">
                        ⭐ PREMIUM
                      </span>
                    )}
                  </div>
                  <p className="text-sm text-slate-600 mb-2">{tool.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {tool.tags.map((tag) => (
                      <span key={tag} className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded">
                        {tag}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="ml-4 text-primary-600 group-hover:translate-x-1 transition-transform">
                  →
                </div>
              </Link>
            ))}
          </div>
        </section>

        {/* Features */}
        <section className="mb-12">
          <h2 className="text-3xl font-black text-gray-900 mb-8 text-center">Why Use Our Tools?</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl border-2 border-green-200 shadow-xl p-8 hover:shadow-2xl transition-shadow">
              <div className="text-5xl mb-4">✓</div>
              <h3 className="font-black text-xl text-gray-900 mb-3">100% Free Forever</h3>
              <p className="text-sm text-slate-600">
                No signup, no credit card, no hidden fees. All tools are completely free to use as often as you need.
              </p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-3xl border-2 border-blue-200 shadow-xl p-8 hover:shadow-2xl transition-shadow">
              <div className="text-5xl mb-4">🔒</div>
              <h3 className="font-black text-xl text-gray-900 mb-3">Privacy First</h3>
              <p className="text-base text-gray-700">
                Your data stays in your browser. We don't store, track, or sell your financial information.
              </p>
            </div>

            <div className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-3xl border-2 border-purple-200 shadow-xl p-8 hover:shadow-2xl transition-shadow">
              <div className="text-5xl mb-4">📱</div>
              <h3 className="font-black text-xl text-gray-900 mb-3">Works Everywhere</h3>
              <p className="text-base text-gray-700">
                Mobile-friendly design works on any device. Calculate taxes on the go or print records for your files.
              </p>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-amber-50 rounded-3xl border-2 border-yellow-200 shadow-xl p-8 hover:shadow-2xl transition-shadow">
              <div className="text-5xl mb-4">⚡</div>
              <h3 className="font-black text-xl text-gray-900 mb-3">Instant Results</h3>
              <p className="text-base text-gray-700">
                Real-time calculations as you type. See exactly how changes affect your tax liability immediately.
              </p>
            </div>

            <div className="bg-gradient-to-br from-teal-50 to-cyan-50 rounded-3xl border-2 border-teal-200 shadow-xl p-8 hover:shadow-2xl transition-shadow">
              <div className="text-5xl mb-4">📊</div>
              <h3 className="font-black text-xl text-gray-900 mb-3">IRS-Compliant</h3>
              <p className="text-base text-gray-700">
                Based on current IRS tax tables, standard deduction amounts, and official mileage rates for {new Date().getFullYear()}-{new Date().getFullYear() + 1}.
              </p>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-3xl border-2 border-orange-200 shadow-xl p-8 hover:shadow-2xl transition-shadow">
              <div className="text-5xl mb-4">🎓</div>
              <h3 className="font-black text-xl text-gray-900 mb-3">Educational Resources</h3>
              <p className="text-base text-gray-700">
                Each tool includes guides, FAQs, and blog posts to help you understand the tax implications.
              </p>
            </div>
          </div>
        </section>

        {/* Who is this for */}
        <section className="bg-gradient-to-br from-slate-50 to-gray-100 rounded-3xl border-2 border-gray-200 shadow-xl p-10 mb-12">
          <h2 className="text-3xl font-black text-gray-900 mb-8 text-center">Who Are These Tools For?</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            <div>
              <div className="text-3xl mb-2">👷</div>
              <div className="font-semibold text-slate-900 text-sm">Contractors</div>
            </div>
            <div>
              <div className="text-3xl mb-2">💻</div>
              <div className="font-semibold text-slate-900 text-sm">Freelancers</div>
            </div>
            <div>
              <div className="text-3xl mb-2">🎨</div>
              <div className="font-semibold text-slate-900 text-sm">Consultants</div>
            </div>
            <div>
              <div className="text-3xl mb-2">🚗</div>
              <div className="font-semibold text-slate-900 text-sm">Rideshare Drivers</div>
            </div>
            <div>
              <div className="text-3xl mb-2">🏠</div>
              <div className="font-semibold text-slate-900 text-sm">Real Estate Agents</div>
            </div>
            <div>
              <div className="text-3xl mb-2">📸</div>
              <div className="font-semibold text-slate-900 text-sm">Photographers</div>
            </div>
            <div>
              <div className="text-3xl mb-2">✍️</div>
              <div className="font-semibold text-slate-900 text-sm">Writers</div>
            </div>
            <div>
              <div className="text-3xl mb-2">🎯</div>
              <div className="font-semibold text-slate-900 text-sm">Side Hustlers</div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-lg p-8 text-white text-center mb-12">
          <h2 className="text-2xl font-bold mb-3">Start with Our Tax Calculator</h2>
          <p className="mb-6 text-primary-50">
            Calculate your federal, state, and self-employment taxes in under 2 minutes
          </p>
          <Link
            to="/"
            className="inline-block bg-white text-primary-700 px-8 py-3 rounded-lg font-semibold hover:bg-primary-50 transition-colors"
          >
            Calculate My Taxes →
          </Link>
        </div>

        {/* Recent Posts */}
        <RecentPosts />
      </div>
    </div>
    </>
  );
}
